import React, { useState } from "react";
import * as m from "../../lib/model";
import { MoneyInput } from "../shared";

export const DEBT_STAGES = ["Contacted", "NDA", "Materials Sent", "Term Sheet", "Negotiating", "Committed", "Closed", "Passed"];
export const LENDER_TYPES = ["Bank", "SBA Lender", "Credit Union", "Private Credit", "Mezzanine", "SBIC", "Family Office", "Seller Financing", "Other"];
export const RECOURSE = ["Full recourse", "Limited recourse", "Non-recourse"];
export const FACILITIES = ["Senior term loan", "SBA 7(a)", "SBA 504", "Revolver", "Unitranche", "Mezzanine / sub debt", "Seller note", "Equipment loan", "CRE mortgage"];

const Txt = ({ label, k, form, set, type = "text", placeholder, hint, testid }) => (
  <div className="field"><label>{label}{hint ? <span className="blend-hint">{hint}</span> : null}</label>
    {type === "money"
      ? <MoneyInput value={form[k] ?? ""} onChange={(v) => set(k, v === "" ? "" : +v)} placeholder={placeholder} testid={testid || `dl-${k}`} />
      : <input className="inp" type={type} step={type === "number" ? "any" : undefined} value={form[k] ?? ""} placeholder={placeholder} onChange={(e) => set(k, type === "number" ? (e.target.value === "" ? "" : +e.target.value) : e.target.value)} data-testid={testid || `dl-${k}`} />}</div>
);
const Sel = ({ label, k, form, set, opts }) => (
  <div className="field"><label>{label}</label>
    <select className="inp" value={form[k] ?? opts[0]} onChange={(e) => set(k, e.target.value)} data-testid={`dl-${k}`}>{opts.map((o) => <option key={o}>{o}</option>)}</select></div>
);
export const YESNO = ["", "Yes", "No"];
const YN = ({ label, k, form, set }) => (
  <div className="field"><label>{label}</label>
    <select className="inp" value={form[k] === "Yes" || form[k] === "No" ? form[k] : ""} onChange={(e) => set(k, e.target.value)} data-testid={`dl-${k}`}>
      {YESNO.map((o) => <option key={o || "none"} value={o}>{o || "—"}</option>)}
    </select></div>
);
const Sec = ({ n, title, sub, children }) => (
  <div className="ts-sec"><div className="ts-sec-head"><span className="ts-n">{n}</span><b>{title}</b>{sub && <small>{sub}</small>}</div><div className="cm-form-grid">{children}</div></div>
);

export const DebtModal = ({ open, initial, dealKey, dealName, ebitda, targetDebt, cfads = 0, users = [], onClose, onSave }) => {
  const [form, setForm] = useState(initial || {});
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (open) setForm(initial || { type: "Bank", stage: "Contacted", rateType: "Fixed", recourse: "Full recourse", facility: "Senior term loan", termYears: 10, amortYears: 10, closingDays: 60, amount: targetDebt || 0 }); }, [open, initial, targetDebt]);
  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const lev = ebitda > 0 && form.amount ? form.amount / ebitda : 0;
  const ds = form.amount > 0 && form.rate > 0 ? m.amortMonthly(+form.amount, +form.rate, +form.amortYears || +form.termYears || 10) * 12 : 0;
  const dscr = ds > 0 && cfads > 0 ? cfads / ds : 0;
  const submit = async (e) => {
    e.preventDefault();
    if (!form.lender?.trim()) return;
    setBusy(true);
    try { await onSave({ ...form, dealKey, dealName }); onClose(); } finally { setBusy(false); }
  };
  return (
    <div className="modal-overlay" data-testid="debt-modal" onMouseDown={onClose}>
      <form className="modal-card ts-modal" onMouseDown={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="ts-head">
          <div><div className="modal-title" style={{ marginBottom: 2 }}>{form.id ? "Lender term sheet" : "Add lender"}</div><div className="modal-sub" style={{ marginBottom: 0 }}>{dealName} — capture the proposal exactly as the banker sent it; the comparison ranks it automatically.</div></div>
          <div className="ts-live" data-testid="dl-preview">
            <div><span>Leverage</span><b>{lev ? `${lev.toFixed(2)}×` : "—"}</b></div>
            <div><span>Annual DS</span><b>{ds ? m.fmt$(ds) : "—"}</b></div>
            <div><span>DSCR</span><b className={dscr ? (m.dscrTone(dscr) === "go" ? "pos" : m.dscrTone(dscr) === "pass" ? "neg" : "") : ""}>{dscr ? m.fmtX(dscr) : "—"}</b></div>
            <div><span>Fees</span><b>{form.amount && form.feesPct ? m.fmt$((+form.feesPct / 100) * +form.amount) : "—"}</b></div>
          </div>
        </div>

        <Sec n="1" title="Lender & banker" sub="who we're talking to">
          <Txt label="Lender / institution *" k="lender" form={form} set={set} placeholder="Frost Bank" />
          <Sel label="Lender type" k="type" form={form} set={set} opts={LENDER_TYPES} />
          <Sel label="Stage" k="stage" form={form} set={set} opts={DEBT_STAGES} />
          <Txt label="Banker name" k="contact" form={form} set={set} placeholder="Maria Lopez" />
          <Txt label="Banker title" k="bankerTitle" form={form} set={set} placeholder="SVP, Commercial Lending" />
          <Txt label="Banker email" k="email" form={form} set={set} type="email" />
          <Txt label="Banker phone" k="phone" form={form} set={set} />
          <div className="field"><label>Owner (our side)</label>
            <select className="inp" value={form.owner || ""} onChange={(e) => set("owner", e.target.value)} data-testid="dl-owner">
              <option value="">Unassigned</option>{users.map((u) => <option key={u.id} value={u.name || u.email}>{u.name || u.email}</option>)}
            </select></div>
          <Txt label="Source" k="source" form={form} set={set} placeholder="Referral, Debt Lenders CRM…" />
        </Sec>

        <Sec n="2" title="Facility & pricing" sub="the money and what it costs">
          <Sel label="Facility" k="facility" form={form} set={set} opts={FACILITIES} />
          <Txt label="Loan amount ($)" k="amount" form={form} set={set} type="money" hint={lev ? `${lev.toFixed(2)}× EBITDA` : targetDebt ? `need ${m.fmt$(targetDebt)}` : ""} />
          <Txt label="All-in rate (%)" k="rate" form={form} set={set} type="number" />
          <Sel label="Rate type" k="rateType" form={form} set={set} opts={["Fixed", "Floating (Prime +)", "Floating (SOFR +)"]} />
          <Txt label="Index" k="indexRate" form={form} set={set} placeholder="Prime / 1M SOFR" />
          <Txt label="Spread (%)" k="spread" form={form} set={set} type="number" />
          <Txt label="Term (years)" k="termYears" form={form} set={set} type="number" />
          <Txt label="Amortization (years)" k="amortYears" form={form} set={set} type="number" />
          <Txt label="Fees (% of loan)" k="feesPct" form={form} set={set} type="number" />
        </Sec>

        <Sec n="3" title="Structure & protections" sub="what they want from us">
          <Sel label="Recourse" k="recourse" form={form} set={set} opts={RECOURSE} />
          <YN label="Personal guarantee" k="personalGuarantee" form={form} set={set} />
          <YN label="Collateral required" k="collateral" form={form} set={set} />
          <Txt label="Min DSCR covenant (×)" k="dscrMin" form={form} set={set} type="number" />
          <Txt label="Max leverage covenant (×)" k="maxLeverage" form={form} set={set} type="number" />
          <YN label="Prepayment penalty" k="prepay" form={form} set={set} />
          <YN label="Deposit / treasury req." k="depositReq" form={form} set={set} />
          <div className="field" style={{ gridColumn: "span 2" }}><label>Other covenants</label><input className="inp" value={form.covenants || ""} onChange={(e) => set("covenants", e.target.value)} placeholder="Quarterly reporting, capex cap…" data-testid="dl-covenants" /></div>
        </Sec>

        <Sec n="4" title="Process" sub="timing and paper">
          <Txt label="Time to close (days)" k="closingDays" form={form} set={set} type="number" />
          <Txt label="Term sheet received" k="termReceived" form={form} set={set} type="date" />
          <Txt label="Term sheet expires" k="termExpires" form={form} set={set} type="date" />
          <Txt label="Next follow-up" k="nextFollowUp" form={form} set={set} type="date" />
          <div className="field" style={{ gridColumn: "span 2" }}><label>Notes</label><input className="inp" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="dl-notes" /></div>
        </Sec>
        {!form.id && <div className="cm-empty">Save first, then attach the term sheet PDF from the Term Sheets table or the lender's detail panel.</div>}

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose} data-testid="dl-cancel">Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy || !form.lender?.trim()} data-testid="dl-save">{busy ? "Saving…" : form.id ? "Save term sheet" : "Add lender"}</button>
        </div>
      </form>
    </div>
  );
};
